import React from 'react';
import { X, TrendingUp, AlertCircle, Info, Ruler, Star, BookOpen } from 'lucide-react';
import { calculate3DMovement, calculateGalacticJourney, getMovementDescription, formatCosmicDistance } from '../utils/celestialMovement';
import celebrityStars from '../data/celebrity_stars.json';

/**
 * Calculate print-scale displacement
 * Converts arcsecond angular drift to millimeters on a standard A4 print
 */
const getPrintShift = (arcsec_drift, paper_width_mm = 210) => {
  // 1 degree = 3600 arcseconds
  // If the map spans 1 degree width = paper_width_mm
  const mm_per_arcsec = paper_width_mm / 3600;
  const shift_mm = parseFloat(arcsec_drift) * mm_per_arcsec;
  
  // Convert to human comparisons
  let comparison = '';
  if (shift_mm < 0.1) {
    comparison = 'thickness of a human hair';
  } else if (shift_mm < 0.5) {
    comparison = 'width of a pencil lead';
  } else if (shift_mm < 2) {
    comparison = 'thickness of a coin';
  } else if (shift_mm < 5) {
    comparison = 'width of a pencil';
  } else {
    comparison = 'width of your thumb';
  }
  
  return {
    mm: shift_mm.toFixed(2),
    inches: (shift_mm / 25.4).toFixed(3),
    comparison
  };
};

const StarDetailCard = ({ star, birthDate, onClose, position }) => {
  if (!birthDate) return null;
  
  const birthYear = new Date(birthDate).getFullYear();
  const movement = calculate3DMovement(star, birthYear);
  const userJourney = calculateGalacticJourney(birthDate);
  
  // Get celebrity lore if star is famous
  const celebrityData = star.common_name ? celebrityStars[star.common_name] : null;
  
  // Calculate print shift
  const printShift = movement ? getPrintShift(movement.angular_drift_arcsec) : null;
  
  // Determine radial velocity direction
  let radialDirection = null;
  if (star.radial_velocity != null) {
    if (star.radial_velocity > 0) {
      radialDirection = { text: 'Fleeing from us', emoji: '🏃‍♂️', color: 'text-red-400' };
    } else if (star.radial_velocity < 0) {
      radialDirection = { text: 'Approaching us', emoji: '🎯', color: 'text-green-400' };
    } else {
      radialDirection = { text: 'Perpendicular motion', emoji: '↔️', color: 'text-blue-400' };
    }
  }
  
  // If no movement data available
  if (!movement) {
    return (
      <div 
        className="fixed z-50 bg-slate-900 border-2 border-red-500/50 rounded-xl shadow-2xl p-6 max-w-md backdrop-blur-xl"
        style={{ 
          left: position?.x || '50%', 
          top: position?.y || '50%',
          transform: position ? 'none' : 'translate(-50%, -50%)'
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-unbounded text-lg text-white flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-400" />
            Insufficient Data
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>
        <p className="text-sm text-slate-300">
          Movement data not available for this object. Proper motion or distance measurements are missing from Gaia DR3.
        </p>
      </div>
    );
  }
  
  return (
    <div 
      className="fixed z-50 bg-slate-900/95 backdrop-blur-xl border-2 border-cyan-400/60 rounded-2xl shadow-2xl max-w-2xl overflow-hidden"
      style={{ 
        left: position?.x ? Math.min(position.x, window.innerWidth - 700) : '50%',
        top: position?.y ? Math.min(position.y, window.innerHeight - 600) : '50%',
        transform: (!position?.x || !position?.y) ? 'translate(-50%, -50%)' : 'none',
        maxHeight: '85vh',
        background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.98) 0%, rgba(30, 41, 59, 0.98) 100%)'
      }}
    >
      {/* Glassmorphism Header */}
      <div className="relative bg-gradient-to-r from-cyan-900/50 via-blue-900/50 to-purple-900/50 p-6 border-b border-cyan-400/30 backdrop-blur-lg">
        <div className="absolute inset-0 bg-white/5" />
        <div className="relative z-10 flex items-start justify-between">
          <div className="flex-1">
            {/* Star Name & Title */}
            <div className="flex items-center gap-3 mb-2">
              {celebrityData && <Star className="w-6 h-6 text-yellow-400" />}
              <h3 className="font-unbounded text-2xl text-white">
                {star.common_name || 'Unnamed Star'}
              </h3>
            </div>
            
            {celebrityData && (
              <p className="text-cyan-300 text-sm mb-2">{celebrityData.title}</p>
            )}
            
            {/* Constellation & Designation */}
            <div className="flex flex-wrap gap-3 text-sm">
              {star.constellation && (
                <span className="px-3 py-1 bg-indigo-500/30 border border-indigo-400/40 rounded-full text-indigo-200">
                  {star.constellation}
                </span>
              )}
              {celebrityData?.bayer && (
                <span className="px-3 py-1 bg-purple-500/30 border border-purple-400/40 rounded-full text-purple-200">
                  {celebrityData.bayer}
                </span>
              )}
            </div>
            
            {star.common_name && (
              <p className="text-xs text-slate-400 font-mono mt-2">{star.source_id}</p>
            )}
          </div>
          
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors backdrop-blur-sm"
          >
            <X className="w-6 h-6 text-slate-300" />
          </button>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="p-6 space-y-6 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 150px)' }}>
        
        {/* Celebrity Lore Section */}
        {celebrityData && (
          <div className="p-5 bg-gradient-to-br from-yellow-900/20 to-amber-900/20 border border-yellow-500/30 rounded-xl backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-3">
              <BookOpen className="w-5 h-5 text-yellow-400" />
              <h4 className="font-semibold text-yellow-200">Cosmic Celebrity</h4>
            </div>
            <ul className="space-y-2 text-sm text-yellow-100/90">
              {celebrityData.facts.map((fact, idx) => (
                <li key={idx} className="flex gap-2">
                  <span className="text-yellow-400">✨</span>
                  <span>{fact}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* User's Galactic Journey - TOP PRIORITY */}
        <div className="p-5 bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-400/40 rounded-xl backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-500/30 rounded-full flex items-center justify-center border border-purple-400/50">
              <span className="text-xl">🚀</span>
            </div>
            <div>
              <div className="text-base font-semibold text-purple-100">Your Galactic Journey</div>
              <div className="text-xs text-purple-300/70">Since {birthYear}</div>
            </div>
          </div>
          <div className="text-center py-4 px-4 bg-black/40 rounded-lg mb-3 border border-purple-500/20">
            <div className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-200 to-pink-200">
              {userJourney.distance_billion_km} Billion km
            </div>
          </div>
          <p className="text-sm text-purple-200/90 leading-relaxed">
            While this star moved through space, <span className="text-purple-300 font-semibold">you traveled {userJourney.distance_billion_km} billion km</span> around 
            the Milky Way&apos;s center at 230 km/s (514,000 mph).
          </p>
        </div>
        
        {/* Print Scale Translation - THE KILLER FEATURE */}
        {printShift && (
          <div className="p-5 bg-gradient-to-r from-orange-900/30 to-red-900/30 border border-orange-400/40 rounded-xl backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-3">
              <Ruler className="w-5 h-5 text-orange-400" />
              <h4 className="font-semibold text-orange-200">On an A4 Print</h4>
            </div>
            <div className="space-y-3">
              <div className="text-center py-3 bg-black/40 rounded-lg border border-orange-500/20">
                <div className="text-2xl font-bold text-white mb-1">
                  {printShift.mm} mm
                </div>
                <div className="text-xs text-orange-300/70">
                  ({printShift.inches} inches)
                </div>
              </div>
              <p className="text-sm text-orange-100/90 leading-relaxed">
                📏 If you printed this star map on standard A4 paper, this star has physically shifted 
                <span className="text-orange-300 font-semibold"> {printShift.mm} millimeters</span> since your birth.
              </p>
              <p className="text-xs text-orange-200/80 italic">
                💡 That&apos;s roughly the {printShift.comparison}
              </p>
            </div>
          </div>
        )}
        
        {/* Star's Movement */}
        <div className="p-5 bg-slate-800/60 backdrop-blur-sm rounded-xl border border-cyan-400/30">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-cyan-400" />
            <h4 className="font-unbounded text-lg text-white">Stellar Movement</h4>
          </div>
          
          {/* Positions Comparison */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
              <div className="text-xs text-slate-400 mb-2">Birth Position</div>
              <div className="text-xs space-y-1">
                <div><span className="text-slate-500">RA:</span> <span className="text-white font-mono">{star.ra.toFixed(4)}°</span></div>
                <div><span className="text-slate-500">Dec:</span> <span className="text-white font-mono">{star.dec.toFixed(4)}°</span></div>
              </div>
              <div className="text-xs text-slate-500 mt-1">{birthDate}</div>
            </div>
            
            <div className="p-3 bg-cyan-900/30 rounded-lg border border-cyan-500/30">
              <div className="text-xs text-cyan-300 mb-2">Current Position</div>
              <div className="text-xs space-y-1">
                <div><span className="text-cyan-400">RA:</span> <span className="text-white font-mono">{(star.ra + (movement.angular_drift_arcsec / 3600)).toFixed(4)}°</span></div>
                <div><span className="text-cyan-400">Dec:</span> <span className="text-white font-mono">{star.dec.toFixed(4)}°</span></div>
              </div>
              <div className="text-xs text-cyan-400 mt-1">Today</div>
            </div>
          </div>
          
          {/* Angular Drift */}
          <div className="p-4 bg-gradient-to-r from-cyan-900/40 to-blue-900/40 border border-cyan-500/40 rounded-lg mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-cyan-300">Angular Drift</span>
              <span className="text-2xl font-bold text-white">{movement.angular_drift_arcsec}&quot;</span>
            </div>
            <div className="text-xs text-cyan-300/70">arcseconds shift since your birth</div>
          </div>
          
          {/* 3D Distance Traveled */}
          <div className="p-4 bg-gradient-to-br from-blue-900/40 to-purple-900/40 border border-blue-500/40 rounded-lg mb-3">
            <div className="text-sm text-blue-300 mb-3 font-semibold">Distance Traveled Through Space</div>
            
            {movement.has_3d_data ? (
              <div className="space-y-2">
                {/* Radial Direction */}
                {radialDirection && (
                  <div className={`flex items-center gap-2 mb-3 p-2 bg-black/30 rounded ${radialDirection.color}`}>
                    <span className="text-lg">{radialDirection.emoji}</span>
                    <span className="text-sm font-medium">{radialDirection.text} at {Math.abs(star.radial_velocity).toFixed(1)} km/s</span>
                  </div>
                )}
                
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Sideways (Transverse)</span>
                  <span className="font-mono text-white text-sm">{formatCosmicDistance(movement.distance_transverse_billion_km)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-slate-400">Toward/Away (Radial)</span>
                  <span className="font-mono text-white text-sm">{formatCosmicDistance(movement.distance_radial_billion_km)}</span>
                </div>
                <div className="pt-3 border-t border-blue-500/30">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-blue-200">Total 3D Distance</span>
                    <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-200 to-blue-200">
                      {formatCosmicDistance(movement.distance_total_billion_km)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-green-400 mt-2 bg-green-900/20 p-2 rounded">
                  <Info className="w-4 h-4" />
                  <span>Full 3D calculation (includes radial velocity)</span>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-blue-200">Transverse Movement</span>
                  <span className="text-xl font-bold text-white">{formatCosmicDistance(movement.distance_transverse_billion_km)}</span>
                </div>
                <div className="flex items-center gap-2 text-xs text-amber-400 mt-3 bg-amber-900/20 p-2 rounded">
                  <AlertCircle className="w-4 h-4" />
                  <span>2D sideways movement only (radial velocity not available from Gaia)</span>
                </div>
              </div>
            )}
          </div>
          
          {/* Velocity */}
          <div className="p-3 bg-slate-900/50 rounded-lg border border-slate-700">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-400">Total Velocity</span>
              <span className="font-mono text-white text-lg">{movement.velocity_total_kms} km/s</span>
            </div>
          </div>
        </div>
        
        {/* Human Scale Context */}
        <div className="p-4 bg-gradient-to-r from-indigo-900/30 to-purple-900/30 border border-indigo-500/30 rounded-lg backdrop-blur-sm">
          <div className="text-xs text-indigo-300/80 mb-2 font-semibold">Cosmic Scale Comparisons:</div>
          <div className="space-y-2 text-xs text-indigo-200/90">
            <div className="flex items-start gap-2">
              <span className="text-indigo-400">🌌</span>
              <span><span className="font-mono text-indigo-300 font-semibold">{movement.solar_system_widths}</span> times the width of our Solar System</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-indigo-400">☀️</span>
              <span><span className="font-mono text-indigo-300 font-semibold">{movement.earth_sun_trips}</span> round trips from Earth to the Sun</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="text-indigo-400">⏱️</span>
              <span>In <span className="font-semibold">{movement.years_elapsed} years</span> since your birth</span>
            </div>
          </div>
        </div>
        
        {/* Glossary Link */}
        <div className="text-center pt-2">
          <button 
            className="text-xs text-cyan-400 hover:text-cyan-300 underline decoration-dotted transition-colors"
            onClick={() => window.open('https://en.wikipedia.org/wiki/Stellar_kinematics', '_blank')}
          >
            📚 Learn More About Stellar Motion & Kinematics
          </button>
        </div>
      </div>
    </div>
  );
};

export default StarDetailCard;
